import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const HeaderUnderline = styled.div`
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  margin: 1rem auto 0;
  border-radius: 2px;
`;

const ContactCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  width: 100%;
  max-width: 1600px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
  }
`;

const ContactCard = styled(motion.div)`
  width: 100%;
  background: white;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 640px) {
    padding: 1.75rem 1.25rem;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    gap: 1rem;
    border-radius: 16px;
  }
`;

const ContactPhoto = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 143, 0, 0.1);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.15);

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }
`;

const ContactHeader = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactName = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const ContactTitle = styled.p`
  font-size: 0.9rem;
  color: var(--gray-600);
  margin: 0;
  font-weight: 500;
`;

const ContactCompany = styled.p`
  font-size: 0.85rem;
  color: var(--primary-color);
  margin: 0;
  font-weight: 600;
`;

const ContactDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f7f9fc 0%, #eef1f7 100%);
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.15);
  text-decoration: none;
  color: inherit;
  transition: all 0.25s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  min-height: 48px;

  &:hover {
    ${props => props.$clickable && `
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
      border-color: rgba(255, 143, 0, 0.3);
    `}
  }

  @media (max-width: 768px) {
    padding: 0.875rem 0.875rem;
    gap: 0.625rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.75rem;
    gap: 0.5rem;
    border-radius: 8px;
  }
`;

const ContactIcon = styled.span`
  font-size: 1.3rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 1.15rem;
    border-radius: 6px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
`;

const ContactLabel = styled.span`
  font-size: 0.7rem;
  color: var(--gray-500);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContactValue = styled.span`
  font-size: 0.85rem;
  color: var(--secondary-color);
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContactPage = () => {
  return (
    <PageContainer>
      <PageHeader>
        <HeaderTitle>Our Team</HeaderTitle>
        <HeaderUnderline />
      </PageHeader>

      <ContactCardContainer>
      <ContactCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <ContactPhoto
          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/files/Robert.jpeg"
          alt="Robert den Hertog"
        />

        <ContactHeader>
          <ContactName>Robert den Hertog</ContactName>
          <ContactTitle>Head of Sales & Marketing</ContactTitle>
          <ContactCompany>Indus Bulk Logistics</ContactCompany>
        </ContactHeader>

        <ContactDetails>
          <ContactItem
            href="tel:+31172727169"
            $clickable={true}
          >
            <ContactIcon>📞</ContactIcon>
            <ContactInfo>
              <ContactLabel>Office Phone</ContactLabel>
              <ContactValue>+31 (0)172 72 71 69</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="tel:+31682276258"
            $clickable={true}
          >
            <ContactIcon>📱</ContactIcon>
            <ContactInfo>
              <ContactLabel>Mobile Phone</ContactLabel>
              <ContactValue>+31 (0)6 82 27 62 58</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="mailto:r.denhertog@indus-bulklogistics.com"
            $clickable={true}
          >
            <ContactIcon>✉️</ContactIcon>
            <ContactInfo>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>r.denhertog@indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.linkedin.com/in/robertdenhertog92/"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </ContactIcon>
            <ContactInfo>
              <ContactLabel>LinkedIn</ContactLabel>
              <ContactValue>View Profile</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.indus-bulklogistics.com"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>🌐</ContactIcon>
            <ContactInfo>
              <ContactLabel>Website</ContactLabel>
              <ContactValue>www.indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem $clickable={false}>
            <ContactIcon>📍</ContactIcon>
            <ContactInfo>
              <ContactLabel>Address</ContactLabel>
              <ContactValue>Botter 8 | 2411 NM, Bodegraven | The Netherlands</ContactValue>
            </ContactInfo>
          </ContactItem>
        </ContactDetails>
      </ContactCard>

      <ContactCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
      >
        <ContactPhoto
          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/files/junior-signature.png"
          alt="Junior van Santbrink"
        />

        <ContactHeader>
          <ContactName>Junior van Santbrink</ContactName>
          <ContactTitle>New Business Developer</ContactTitle>
          <ContactCompany>Indus Bulk Logistics</ContactCompany>
        </ContactHeader>

        <ContactDetails>
          <ContactItem
            href="tel:+31172727169"
            $clickable={true}
          >
            <ContactIcon>📞</ContactIcon>
            <ContactInfo>
              <ContactLabel>Office Phone</ContactLabel>
              <ContactValue>+31 (0)172 72 71 69</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="tel:+31647854673"
            $clickable={true}
          >
            <ContactIcon>📱</ContactIcon>
            <ContactInfo>
              <ContactLabel>Mobile Phone</ContactLabel>
              <ContactValue>+31 (0)6 47 85 46 73</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="mailto:j.vansantbrink@indus-bulklogistics.com"
            $clickable={true}
          >
            <ContactIcon>✉️</ContactIcon>
            <ContactInfo>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>j.vansantbrink@indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.linkedin.com/in/junior-van-santbrink-0173001a6?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </ContactIcon>
            <ContactInfo>
              <ContactLabel>LinkedIn</ContactLabel>
              <ContactValue>View Profile</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.indus-bulklogistics.com"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>🌐</ContactIcon>
            <ContactInfo>
              <ContactLabel>Website</ContactLabel>
              <ContactValue>www.indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem $clickable={false}>
            <ContactIcon>📍</ContactIcon>
            <ContactInfo>
              <ContactLabel>Address</ContactLabel>
              <ContactValue>Botter 8 | 2411 NM, Bodegraven | The Netherlands</ContactValue>
            </ContactInfo>
          </ContactItem>
        </ContactDetails>
      </ContactCard>

      <ContactCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
      >
        <ContactPhoto
          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/files/Laurens%20Veldman.jpg"
          alt="Laurens Veldman"
        />

        <ContactHeader>
          <ContactName>Laurens Veldman</ContactName>
          <ContactTitle>Partner Manager</ContactTitle>
          <ContactCompany>Indus Bulk Logistics</ContactCompany>
        </ContactHeader>

        <ContactDetails>
          <ContactItem
            href="tel:+31172727169"
            $clickable={true}
          >
            <ContactIcon>📞</ContactIcon>
            <ContactInfo>
              <ContactLabel>Office Phone</ContactLabel>
              <ContactValue>+31 (0)172 72 71 69</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="tel:+31612320361"
            $clickable={true}
          >
            <ContactIcon>📱</ContactIcon>
            <ContactInfo>
              <ContactLabel>Mobile Phone</ContactLabel>
              <ContactValue>+31 (0)6 12 32 03 61</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="mailto:l.veldman@indus-bulklogistics.com"
            $clickable={true}
          >
            <ContactIcon>✉️</ContactIcon>
            <ContactInfo>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>l.veldman@indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://linkedin.com/in/laurens-helenius-veldman-8a4a0b242"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </ContactIcon>
            <ContactInfo>
              <ContactLabel>LinkedIn</ContactLabel>
              <ContactValue>View Profile</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.indus-bulklogistics.com"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>🌐</ContactIcon>
            <ContactInfo>
              <ContactLabel>Website</ContactLabel>
              <ContactValue>www.indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem $clickable={false}>
            <ContactIcon>📍</ContactIcon>
            <ContactInfo>
              <ContactLabel>Address</ContactLabel>
              <ContactValue>Botter 8 | 2411 NM, Bodegraven | The Netherlands</ContactValue>
            </ContactInfo>
          </ContactItem>
        </ContactDetails>
      </ContactCard>

      <ContactCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
      >
        <ContactPhoto
          src="https://i.imgur.com/ePgv7vt.png"
          alt="Arjan Visscher"
        />

        <ContactHeader>
          <ContactName>Arjan Visscher</ContactName>
          <ContactTitle>International Accountmanager</ContactTitle>
          <ContactCompany>Indus Bulk Logistics</ContactCompany>
        </ContactHeader>

        <ContactDetails>
          <ContactItem
            href="tel:+31172727169"
            $clickable={true}
          >
            <ContactIcon>📞</ContactIcon>
            <ContactInfo>
              <ContactLabel>Office Phone</ContactLabel>
              <ContactValue>+31 (0)172 72 71 69</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="tel:+31683500448"
            $clickable={true}
          >
            <ContactIcon>📱</ContactIcon>
            <ContactInfo>
              <ContactLabel>Mobile Phone</ContactLabel>
              <ContactValue>+31 (0)6 83 50 04 48</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="mailto:a.visscher@indus-bulklogistics.com"
            $clickable={true}
          >
            <ContactIcon>✉️</ContactIcon>
            <ContactInfo>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>a.visscher@indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://nl.linkedin.com/in/arjan-visscher"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </ContactIcon>
            <ContactInfo>
              <ContactLabel>LinkedIn</ContactLabel>
              <ContactValue>View Profile</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem
            href="https://www.indus-bulklogistics.com"
            target="_blank"
            rel="noopener noreferrer"
            $clickable={true}
          >
            <ContactIcon>🌐</ContactIcon>
            <ContactInfo>
              <ContactLabel>Website</ContactLabel>
              <ContactValue>www.indus-bulklogistics.com</ContactValue>
            </ContactInfo>
          </ContactItem>

          <ContactItem $clickable={false}>
            <ContactIcon>📍</ContactIcon>
            <ContactInfo>
              <ContactLabel>Address</ContactLabel>
              <ContactValue>Botter 8 | 2411 NM, Bodegraven | The Netherlands</ContactValue>
            </ContactInfo>
          </ContactItem>
        </ContactDetails>
      </ContactCard>
    </ContactCardContainer>
    </PageContainer>
  );
};

export default ContactPage;
